/**
 * The land verb.
 *
 * LAND is an affordance of the SIM, beside HET and fire: the body the registry
 * focuses is the body the ship is at, and pressing LAND takes the ground under
 * the ship and hands it to the Atlas's own `goTo` at the top of ORBIT. Nothing
 * is mounted to get there - the world is already on screen. The press hands the
 * camera from the chase rig to the Atlas's own, shows the host the mount lives
 * in, and the ship stays in its bubble at its own heliocentric position for the
 * whole trip: the sim's tick simply stops integrating while the Atlas has the
 * camera, the way it already does with the menu open.
 *
 * The Atlas's own DOM - wheel, breadcrumb, panels - lives in a full viewport
 * element outside `#app`, so the stage's rect is the whole canvas rather than
 * the menu pane's. The Solar System tab is the same mount re-hosted into the
 * pane (`renderAtlasPane`), so the two entries are two HOSTS for one subtree,
 * never two mounts and never two scenes.
 */

import * as THREE from 'three';

import { renderAtlasTab } from '../atlas.ts';
import { altitudeForTier, bandEdges, type BandEdges } from '../atlas/camera.ts';
import { bodyFixedQuaternion, eclipticToScene } from '../atlas/orrery.ts';
import type { AtlasGroundGame } from '../atlas/groundGame.ts';
import { setMode as setAppMode } from '../appMode.ts';
import { track } from '../visits/bus.ts';
import { devFlag, urlFlag, TEST_SWITCHES } from '../urlFlags.ts';
import { bodyById } from '../world/bodies.ts';
import { gameClock } from '../world/clock.ts';
import type { WorldVec } from '../world/frame.ts';
import type { SubtreeHandle } from '../world/scene.ts';
import type { BodyRow } from '../world/types.ts';
import type { BodyPlaces } from './bodies.ts';
import type { FlightState } from './flight.ts';
import type { SimPositionHandle } from './position.ts';

/** Where a press would put the camera, and the band edges it descends through. */
export interface LandTarget {
  readonly bodyId: string;
  readonly displayName: string;
  readonly lat: number;
  readonly lon: number;
  readonly altM: number;
  readonly edges: BandEdges;
  /** Is this the hull's own contact point rather than a descent from cruise? */
  readonly grounded: boolean;
}

export type LandVerdict =
  | { ok: true; at: LandTarget }
  | { ok: false; label: string; why: string };

/** The Atlas's own camera handle, published on the shared scene at mount. */
export interface AtlasCameraHandle {
  readonly altitude: number;
  readonly tier: number;
  setAltitude(m: number): void;
  goTo(bodyId: string, lat: number | null, lon: number | null, altM: number): void;
  setGroundAnchor(next: { bodyId: string; lat: number; lon: number } | null): void;
  landCommitted?(): void;
  shipFocus?(at: { bodyId: string; lat: number; lon: number } | null): void;
}

/** The same mount's world side: the bodies drawn for a camera it does not own. */
export interface AtlasWorldHandle {
  readonly external: boolean;
  readonly ready: boolean;
  setExternal(on: boolean): void;
  setShipFocus(id: string | null): void;
  /** `look`: how much of that body's own look is drawn over the orrery's globe, 0 to 1. */
  setWorldBody(id: string | null, look?: number): void;
  requestBodySets(id: string | null): void;
  setWorldEpoch(jd: number | null): void;
  drawnSurfaceRadiusM(id: string, dir: WorldVec): number | null;
  restingGroundRadiusM(id: string, dir: WorldVec): number | null;
  shapeSurfaceRadiusM(id: string, dir: WorldVec): number | null;
  shapeOuterRadiusM(id: string): number | null;
  reliefSurfaceRadiusM(id: string, dir: WorldVec): number | null;
  reliefOuterRadiusM(id: string): number | null;
  globeMapWidth(id: string): number | null;
  setShipMarkerVisible(on: boolean): void;
  attach(node: THREE.Object3D, at: () => WorldVec): () => void;
  update(camWorld: WorldVec, sceneX: number, sceneY: number, sceneZ: number,
    drawCamera: THREE.Camera | null): void;
}

/**
 * The rung a landing opens on: the Atlas's own top of ORBIT.
 *
 * `altitudeForTier(1, ...)` is the hemisphere at 3.5 radii that every other
 * entry into the Atlas opens a body at, and it is what the ladder's first rung
 * reframes to when the registry lands. Asking for anything else here is asking
 * for a framing the Atlas will take back on its next `applyState`, which is a
 * band-wide jump on the first drawn frames of the descent.
 */
const LAND_ENTRY_TIER = 1;
/**
 * `?prehandoff=1`: the land verb as it stood before the hand-off - the FOCUSED
 * body, the tier entry altitude, the surveyed site's own aim, and no way out
 * of the ladder but back to a grounded ship.
 * `grounded-handoff-check.mjs --inject pre-handoff` reaches it; nothing a
 * player presses does.
 */
export const preHandoff = devFlag(() => TEST_SWITCHES && urlFlag('prehandoff'));
/**
 * `?groundplotrung=1`: the grounded ask as it stood before this fix - the
 * ladder's flat 1,500 m ground tier, in the PLOT band rather than SURFACE.
 * `grounded-handoff-check.mjs --inject ground-plot-rung` reaches it; nothing a
 * player presses does.
 */
const groundPlotRung = devFlag(() => TEST_SWITCHES && urlFlag('groundplotrung'));
/**
 * `?landlabelunlatched=1`: the LAND button's body pick with no hysteresis -
 * whichever disc is largest THIS frame, unlatched from whichever was named
 * last frame. `descent-dojo.mjs --inject land-label-unlatched` reaches it;
 * nothing a player presses does.
 */
export const landLabelUnlatched = devFlag(() => TEST_SWITCHES && urlFlag('landlabelunlatched'));
/** The Atlas settling after a mount must not read as a wheel back out. */
const LAND_RETURN_GRACE_MS = 600;
/** How long the rung the press asked for is re-applied while the Atlas loads. */
const LAND_ENTRY_TIMEOUT_MS = 8000;


export interface LandVerbDeps {
  simPos: SimPositionHandle;
  flight: FlightState;
  simWorld: SubtreeHandle;
  attemptLaunch: () => void;
  atlasCamera: () => AtlasCameraHandle | null;
  atlasWorld: () => AtlasWorldHandle | null;
  LAND_FRAME_PULL: boolean;
  LAUNCH_KEY: string;
  /** The ground game the boot mounts the Atlas with. */
  readonly atlasGround: AtlasGroundGame;
  /** Live reads: main.ts still owns these. */
  readonly simBodies: BodyPlaces | null;
  readonly bodyRows: readonly BodyRow[];
  readonly frameParentId: string | null;
  readonly seedJd: number;
  readonly hudButtons: { updateLandButton(): void };
}

export interface LandVerbHandle {
  /** Is the Atlas holding the camera on a landing right now? */
  readonly open: boolean;
  /**
   * The ship's place in its landed parent's frame, snapshotted once when LAND
   * opens, and the body it is measured from. Null while the verb is shut.
   */
  readonly parentOffset: WorldVec | null;
  readonly parentId: string | null;
  /** The epoch `parentOffset` was captured at, so a caller can turn it
   *  forward with the body's own spin. Null while the verb is shut. */
  readonly parentOffsetJd: number | null;
  /** Is LAND live, and on what? The reading the button and the hook share. */
  target(): LandVerdict;
  /** The press: open the ladder, or close one already open. */
  attempt(): void;
  /** The exit, from the button, a launch or the wheel out past ORBIT. */
  returnToShip(): void;
  /** One read a frame while the Atlas has the camera. */
  pollHandover(): void;
}

export function createLandVerb(deps: LandVerbDeps): LandVerbHandle {
  const {
    simPos, flight, simWorld, attemptLaunch, atlasCamera, atlasWorld,
    LAND_FRAME_PULL, LAUNCH_KEY,
  } = deps;
  /**
   * The world, mounted once.
   *
   * The Atlas is what the ship flies in, so it mounts at boot under the one scene
   * root and stays mounted: cruise draws its bodies, sky and sun from the chase
   * camera's pose, and the land verb hands the camera to the Atlas's own rig
   * rather than building a second mount. The host is the viewport and is off
   * screen until the verb opens it.
   */
  const atlasHost = document.createElement('div');
  atlasHost.id = 'land-host';
  atlasHost.className = 'land-host';
  atlasHost.style.display = 'none';
  document.body.appendChild(atlasHost);
  const atlasDispose = renderAtlasTab(atlasHost, deps.atlasGround);
  window.addEventListener('pagehide', () => atlasDispose(), { once: true });
  let landReturnBtn: HTMLElement | null = null;
  let landLaunchBtn: HTMLElement | null = null;
  let landOpen = false;
  /**
   * The ship's place in its landed parent's frame, snapshotted once when LAND
   * opens. The landed tick re-derives `simPos.world` from this plus the
   * parent's current place instead of summing `frameParentStep()` every tick,
   * so a long stay cannot sum N noisy steps into a drift.
   */
  let landParentOffset: WorldVec | null = null;
  /**
   * The body that offset is measured from: the one LAND opened on, held for the
   * stay. Not `frameParentId`, which is the strongest pull and reads Mars
   * anywhere on Phobos, since Phobos's hold radius sits under its own surface.
   */
  let landParentId: string | null = null;
  /** The epoch `landParentOffset` was captured at, so a stay can turn it
   *  forward with the body's own spin instead of holding it inertial. */
  let landParentOffsetJd: number | null = null;
  let landSystemEdgeM = 0;
  let landAltM = 0;
  let landedAtMs = 0;
  /**
   * Whether the wheel-out handover is live yet.
   *
   * The Atlas fetches its own copy of the registry, and until it lands its
   * `radiusM()` is 0, which reads every altitude as SYSTEM. So the way back is
   * armed by the camera reporting an altitude inside the band the press put it
   * in, not by a timer alone.
   */
  let landArmed = false;
  const _landDir = new THREE.Vector3();
  const _landQ = new THREE.Quaternion();

  /**
   * The ground under the ship, as the body's own lat/lon.
   *
   * The direction from the body to the ship, in scene axes, through the WGCCRE
   * rotation `src/atlas/orrery.ts` derives - the same one a click inside the
   * Atlas is read through, so the point a ship lands on is the point the Atlas
   * would name for it. The epoch is the one the bodies are DRAWN at, which is the
   * ship's own seed until round 11 flies it; the picture the player is aiming at
   * is that epoch's, so the sub-ship point has to be too.
   */
  function subShipLatLon(row: BodyRow, at: WorldVec, jdTdb: number): { lat: number; lon: number } {
    const b = eclipticToScene(at, 1);
    const w = eclipticToScene(simPos.world, 1);
    _landDir.set(w.x - b.x, w.y - b.y, w.z - b.z);
    if (_landDir.lengthSq() === 0) return { lat: 0, lon: 0 };
    _landDir.normalize();
    if (bodyFixedQuaternion(row, jdTdb, _landQ)) _landDir.applyQuaternion(_landQ.invert());
    const lat = THREE.MathUtils.radToDeg(Math.asin(THREE.MathUtils.clamp(_landDir.y, -1, 1)));
    let lon = THREE.MathUtils.radToDeg(Math.atan2(-_landDir.z, _landDir.x));
    if (lon < 0) lon += 360;
    return { lat, lon };
  }

  /**
   * Is LAND live, and on what? One reading, used by the button and the hook.
   *
   * A ship at rest lands where it already IS. The focused row is whichever body
   * fills most of the sky, which is the one under the hull on every state a
   * landing can reach, but it is derived from the view rather than from the
   * contact and a hand-off that asked it would be answering the wrong question.
   * The gate is the same one either way: a row with no baked pyramid has no
   * ground to hand off to, so a landing there keeps the stand-off camera.
   */
  function landTarget(): LandVerdict {
    const resting = preHandoff() ? null : flight.grounded;
    const focused = deps.simBodies?.focus();
    const row = resting !== null
      ? bodyById(deps.bodyRows, resting)
      : (focused ? bodyById(deps.bodyRows, focused.row.id) ?? focused.row : undefined);
    if (!row) {
      return { ok: false, label: 'NO BODY', why: resting === null
        ? 'the registry has not landed, so the sim is showing no body to land on'
        : `the registry carries no row "${resting}", so there is no ground under the hull to open` };
    }
    if (!row.availability.descend) {
      return { ok: false, label: 'NO GROUND', why: row.availability.reason };
    }
    const at = deps.simBodies?.worldAt(row.id);
    if (!at) {
      return { ok: false, label: 'NO FIX', why: `the sim has no heliocentric place for ${row.display_name}` };
    }
    const edges = bandEdges(row.radius_m);
    const { lat, lon } = subShipLatLon(
      row, at, Number.isFinite(deps.seedJd) ? deps.seedJd : gameClock().jdTdb());
    const grounded = resting !== null;
    // A hull at rest opens inside the SURFACE band rather than at the ladder's
    // flat 1,500 m PLOT rung: the old ask left `surfaceRegime()` false, so
    // neither `enterPlotRegime()` nor `enterStandingPose()` ever ran and the eye
    // opened near-nadir off the tier table's own pitch, the same after a crash
    // as after a soft landing. A fraction of `edges.PLOT` keeps the ask under
    // the band edge on every body's own width; `plotCamera()` then takes the
    // frame where a site has a mesh, and the focus camera's own 1.7 m standing
    // floor takes it everywhere else, the same floor `surface-clamp --stand`
    // already measures.
    // `?groundplotrung=1` puts the pre-fix ask back - the ladder's own ground
    // tier, tier 6 - for the control that has to show STANDING failing the way
    // it failed before this fix.
    const groundAltM = groundPlotRung() ? altitudeForTier(6, row.radius_m) : edges.PLOT / 50;
    return { ok: true,
      at: { bodyId: row.id, displayName: row.display_name, lat, lon,
        altM: grounded ? groundAltM : altitudeForTier(LAND_ENTRY_TIER, row.radius_m),
        edges, grounded } };
  }

  function enterLand(at: LandTarget): void {
    if (landOpen) return;
    landOpen = true;
    track('land_start', { body_id: at.bodyId });
    track('atlas_focus', { body_id: at.bodyId, body_class: bodyById(deps.bodyRows, at.bodyId)?.class.replace(/^\d_/, '') ?? 'unknown', source: 'land' });
    {
      landParentId = LAND_FRAME_PULL ? deps.frameParentId : at.bodyId;
      const parentAt = landParentId !== null
        ? deps.simBodies?.worldAt(landParentId) ?? null : null;
      landParentOffset = parentAt
        ? { x: simPos.world.x - parentAt.x, y: simPos.world.y - parentAt.y, z: simPos.world.z - parentAt.z }
        : null;
      landParentOffsetJd = landParentOffset !== null && Number.isFinite(deps.seedJd)
        ? deps.seedJd : null;
    }
    // The camera goes back to the Atlas's own rig for the ladder; the world the
    // ship was looking at is the one the descent starts from.
    atlasWorld()?.setExternal(false);
    atlasHost.style.display = '';
    setAppMode('land', true);
    landSystemEdgeM = at.edges.SYSTEM;
    landAltM = at.altM;
    landedAtMs = performance.now();
    landArmed = false;
    addReturnAffordance(atlasHost, at.grounded);
    // Before any frame is drawn: this runs inside the press, and the Atlas draws
    // on demand from the sim's own tick, so no heliocentric frame is ever painted
    // on the way to the ground the ship is over.
    // The pin first: `goTo`'s own reframe reads the descent anchor, and without
    // one held the ladder takes the surveyed site rather than the contact point.
    if (at.grounded) {
      atlasCamera()?.setGroundAnchor({ bodyId: at.bodyId, lat: at.lat, lon: at.lon });
    }
    atlasCamera()?.goTo(at.bodyId, at.lat, at.lon, at.altM);
    // The ground's own reads go out on the press, not on the band edge three
    // rungs down: the claim is what the walkaround is built from.
    atlasCamera()?.landCommitted?.();
    deps.hudButtons.updateLandButton();
  }

  function returnToShip(): void {
    if (!landOpen) return;
    landOpen = false;
    track('launch', { body_id: landParentId ?? 'none' });
    landParentOffset = null;
    landParentId = null;
    landParentOffsetJd = null;
    landReturnBtn?.remove();
    landReturnBtn = null;
    landLaunchBtn?.remove();
    landLaunchBtn = null;
    atlasHost.style.display = 'none';
    setAppMode('land', false);
    // The contact point belongs to this hand-off alone: left pinned, the next
    // descent from cruise would converge on the last landing instead of on the
    // body's own surveyed site.
    atlasCamera()?.setGroundAnchor(null);
    atlasWorld()?.setExternal(true);
    // The sim's subtree takes the root back. The ship is where its parent's own
    // travel left it: the one clock runs on the landed branch, so the carry runs
    // with it and only the flight integrator is held.
    simWorld.activate();
    deps.hudButtons.updateLandButton();
  }

  /**
   * The way back that is not the wheel: a button beside the Atlas's own two.
   *
   * A hand-off from a grounded ship gets a second one. LAUNCH is the verb that
   * already lifts a resting hull and it keeps its key, but the ladder hides the
   * pilot panel that names that key, so the way out of the ground is on screen
   * for as long as the ground is.
   */
  function addReturnAffordance(host: HTMLElement, grounded: boolean): void {
    const bottom = host.querySelector('.atlas-bottom');
    const add = (id: string, cls: string, text: string, title: string, onClick: () => void):
    HTMLElement => {
      const btn = document.createElement('button');
      btn.id = id;
      btn.className = `atlas-zoom-out ${cls}`;
      btn.textContent = text;
      btn.title = title;
      btn.addEventListener('click', onClick);
      if (bottom) bottom.insertBefore(btn, bottom.firstChild);
      else host.appendChild(btn);
      return btn;
    };
    landReturnBtn = add('land-return', 'land-return', '\u2191 Return to Ship',
      'Back to the bridge; wheeling out past the ORBIT edge does the same',
      () => returnToShip());
    if (!grounded || preHandoff()) return;
    landLaunchBtn = add('land-launch', 'land-launch', `\u2191 Launch (${LAUNCH_KEY})`,
      'Off the ground and into orbit about this body, back at the bridge',
      () => attemptLaunch());
  }

  function attemptLand(): void {
    if (landOpen) { returnToShip(); return; }
    const v = landTarget();
    if (!v.ok) return;
    enterLand(v.at);
  }

  /**
   * The handover back, read once a frame while the Atlas has the camera.
   *
   * Wheeling out past the ORBIT edge is the reverse of the press: the band above
   * ORBIT is the sim's, so the ship's own chase rig takes the camera back rather
   * than the Atlas drawing a heliocentric view a ship is already standing in.
   */
  function pollLandHandover(): void {
    if (!landOpen) return;
    const cam = atlasCamera();
    if (!cam) return;
    if (!landArmed) {
      const waited = performance.now() - landedAtMs;
      // The Atlas fetches its own copy of the registry, and until it lands its
      // radius for the body is 0, which reads every altitude as SYSTEM. So the
      // rung the press asked for is set again - a no-op the moment the tier
      // already agrees - rather than the descent starting a band too high.
      if (cam.tier === 0 && waited < LAND_ENTRY_TIMEOUT_MS) cam.setAltitude(landAltM);
      if (waited >= LAND_RETURN_GRACE_MS && cam.tier >= 1 && cam.altitude < landSystemEdgeM) {
        landArmed = true;
      }
      return;
    }
    if (cam.altitude >= landSystemEdgeM) returnToShip();
  }
  return {
    get open(): boolean { return landOpen; },
    get parentOffset(): WorldVec | null { return landParentOffset; },
    get parentId(): string | null { return landParentId; },
    get parentOffsetJd(): number | null { return landParentOffsetJd; },
    target: landTarget,
    attempt: attemptLand,
    returnToShip,
    pollHandover: pollLandHandover,
  };
}
